import moment from 'moment';

/**
 * Common date/datetime field names that should be converted from UTC to local timezone
 */
const DATE_FIELDS = [
  'created_at',
  'updated_at',
  'deleted_at',
  'payment_date',
  'closed_date',
  'start_time',
  'end_time',
  'sent_at',
  'dob',
  'date',
  'datetime',
  'timestamp',
  'day',
  'time_start',
  'time_end',
  'slot',
  // Subscription date fields
  'purchased_at',
  'expires_at',
  'ends_at',
  'cancelled_at',
];

/**
 * Checks if a string is a valid UTC date/datetime string
 * @param {string} value - The value to check
 * @returns {boolean} - True if the value is a valid UTC date string
 */
const isUTCDateString = (value) => {
  if (typeof value !== 'string' || value.length < 10) {
    return false;
  }

  // Check for explicit UTC indicators
  const hasUTCIndicator = value.includes('Z') || value.includes('+00:00') || value.endsWith(' UTC');
  
  // Check for common UTC date formats (ISO 8601, MySQL datetime, etc.)
  const utcPatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO 8601 with time
    /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, // MySQL datetime format
    /^\d{4}-\d{2}-\d{2}$/, // Date only
  ];

  const matchesPattern = utcPatterns.some(pattern => pattern.test(value));
  const isValidUTC = moment.utc(value).isValid();
  
  // If it has UTC indicator, definitely convert it
  // If it matches pattern and is valid UTC, assume it's from API (UTC)
  // We assume dates from APIs are in UTC unless explicitly marked otherwise
  return (hasUTCIndicator || (matchesPattern && isValidUTC));
};

/**
 * Converts a UTC date string to local timezone
 * @param {string} utcDateString - UTC date string
 * @returns {string} - Local date string in the same format
 */
const convertUTCToLocal = (utcDateString) => {
  if (!utcDateString || typeof utcDateString !== 'string') {
    return utcDateString;
  }

  try {
    // Date-only values (YYYY-MM-DD) should not be timezone-shifted.
    // Shifting can move the calendar day depending on local offset.
    const isDateOnly =
      /^\d{4}-\d{2}-\d{2}$/.test(utcDateString) &&
      !utcDateString.includes('T') &&
      !utcDateString.includes(' ');
    if (isDateOnly) {
      const parsed = moment(utcDateString, 'YYYY-MM-DD', true);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : utcDateString;
    }

    // Parse as UTC and convert to local
    const utcMoment = moment.utc(utcDateString);
    
    if (!utcMoment.isValid()) {
      return utcDateString;
    }

    // Convert to local timezone
    const localMoment = utcMoment.local();

    // Preserve the original format
    // If it's a datetime (has time component), return datetime
    // If it's a date only, return date only
    if (utcDateString.includes('T') || utcDateString.includes(' ')) {
      // Has time component - return as ISO string with local timezone
      return localMoment.format('YYYY-MM-DD HH:mm:ss');
    } else {
      // Date only - return as date
      return localMoment.format('YYYY-MM-DD');
    }
  } catch (error) {
    console.error('Error converting UTC to local:', error, utcDateString);
    return utcDateString;
  }
};

/**
 * Recursively converts UTC dates in an object or array to local timezone
 * @param {any} data - The data to process (object, array, or primitive)
 * @param {Set} visited - Set to track visited objects (prevents circular references)
 * @returns {any} - The data with converted dates
 */
const convertDatesInData = (data, visited = new Set()) => {
  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives
  if (typeof data !== 'object') {
    return data;
  }

  // Prevent circular references
  if (visited.has(data)) {
    return data;
  }

  visited.add(data);

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => convertDatesInData(item, visited));
  }

  // Handle objects
  const converted = {};
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];

      // Check if this is a date field or if the value looks like a UTC date string
      if (DATE_FIELDS.includes(key.toLowerCase()) || isUTCDateString(value)) {
        converted[key] = convertUTCToLocal(value);
      } else if (typeof value === 'object' && value !== null) {
        // Recursively process nested objects/arrays
        converted[key] = convertDatesInData(value, visited);
      } else {
        converted[key] = value;
      }
    }
  }

  visited.delete(data);
  return converted;
};

/**
 * Converts a UTC date/datetime to local timezone moment object
 * @param {string|Date|moment.Moment} date - UTC date
 * @returns {moment.Moment} - Local timezone moment object
 */
export const utcToLocalMoment = (date) => {
  if (!date) return null;
  return moment.utc(date).local();
};

/**
 * Formats a UTC date/datetime to local timezone string
 * @param {string|Date|moment.Moment} date - UTC date
 * @param {string} format - Moment format string (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns {string} - Formatted local date string
 */
export const formatUTCDateToLocal = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '';
  return utcToLocalMoment(date).format(format);
};

/**
 * Converts UTC dates in API response data to local timezone
 * This is the main function to use for converting API responses
 * @param {any} data - API response data
 * @returns {any} - Data with converted dates
 */
export const convertAPIDatesToLocal = (data) => {
  if (!data) return data;
  return convertDatesInData(data);
};

/**
 * Checks if a string is a valid local date/datetime string
 * @param {string} value - The value to check
 * @returns {boolean} - True if the value is a valid date string
 */
const isLocalDateString = (value) => {
  if (typeof value !== 'string' || value.length < 10) {
    return false;
  }

  // Check for common date formats (without UTC indicators)
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO 8601 with time
    /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, // MySQL datetime format
    /^\d{4}-\d{2}-\d{2}$/, // Date only
  ];

  const matchesPattern = datePatterns.some(pattern => pattern.test(value));
  const isValid = moment(value).isValid();
  
  // If it doesn't have UTC indicator and matches pattern, assume it's local
  const hasUTCIndicator = value.includes('Z') || value.includes('+00:00') || value.endsWith(' UTC');
  
  return matchesPattern && isValid && !hasUTCIndicator;
};

/**
 * Converts a local date string to UTC
 * @param {string} localDateString - Local date string
 * @returns {string} - UTC date string in the same format
 */
const convertLocalToUTC = (localDateString) => {
  if (!localDateString || typeof localDateString !== 'string') {
    return localDateString;
  }

  try {
    // Date-only values (YYYY-MM-DD) should not be timezone-shifted.
    // Converting local midnight to UTC can change the date (e.g. 27 -> 26).
    const isDateOnly =
      /^\d{4}-\d{2}-\d{2}$/.test(localDateString) &&
      !localDateString.includes('T') &&
      !localDateString.includes(' ');
    if (isDateOnly) {
      const parsed = moment(localDateString, 'YYYY-MM-DD', true);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : localDateString;
    }

    // Parse as local timezone and convert to UTC
    const localMoment = moment(localDateString);
    
    if (!localMoment.isValid()) {
      return localDateString;
    }

    // Convert to UTC
    const utcMoment = localMoment.utc();

    // Preserve the original format
    // If it's a datetime (has time component), return datetime
    // If it's a date only, return date only
    if (localDateString.includes('T') || localDateString.includes(' ')) {
      // Has time component - return as ISO string in UTC
      return utcMoment.format('YYYY-MM-DD HH:mm:ss');
    } else {
      // Date only - return as date (dates don't need timezone conversion)
      return utcMoment.format('YYYY-MM-DD');
    }
  } catch (error) {
    console.error('Error converting local to UTC:', error, localDateString);
    return localDateString;
  }
};

/**
 * Recursively converts local dates in an object or array to UTC timezone
 * @param {any} data - The data to process (object, array, or primitive)
 * @param {Set} visited - Set to track visited objects (prevents circular references)
 * @returns {any} - The data with converted dates
 */
const convertDatesToUTC = (data, visited = new Set()) => {
  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives
  if (typeof data !== 'object') {
    return data;
  }

  // Prevent circular references
  if (visited.has(data)) {
    return data;
  }

  visited.add(data);

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => convertDatesToUTC(item, visited));
  }

  // Handle objects
  const converted = {};
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];

      // Check if this is a date field or if the value looks like a local date string
      if (DATE_FIELDS.includes(key.toLowerCase()) || isLocalDateString(value)) {
        converted[key] = convertLocalToUTC(value);
      } else if (typeof value === 'object' && value !== null) {
        // Recursively process nested objects/arrays
        converted[key] = convertDatesToUTC(value, visited);
      } else {
        converted[key] = value;
      }
    }
  }

  visited.delete(data);
  return converted;
};

/**
 * Converts a local date/datetime to UTC moment object
 * @param {string|Date|moment.Moment} date - Local date
 * @returns {moment.Moment} - UTC timezone moment object
 */
export const localToUTCMoment = (date) => {
  if (!date) return null;
  return moment(date).utc();
};

/**
 * Formats a local date/datetime to UTC timezone string
 * @param {string|Date|moment.Moment} date - Local date
 * @param {string} format - Moment format string (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns {string} - Formatted UTC date string
 */
export const formatLocalDateToUTC = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '';
  return localToUTCMoment(date).format(format);
};

/**
 * Converts local dates in API request data to UTC timezone
 * This is the main function to use for converting API requests
 * @param {any} data - API request data
 * @returns {any} - Data with converted dates
 */
export const convertAPIDatesToUTC = (data) => {
  if (!data) return data;
  return convertDatesToUTC(data);
};

/**
 * Converts a single local date string to UTC
 * Useful for FormData values
 * @param {string} localDateString - Local date string
 * @returns {string} - UTC date string
 */
export const convertLocalDateStringToUTC = (localDateString) => {
  return convertLocalToUTC(localDateString);
};

/**
 * Checks if a string is a local date string
 * @param {string} value - The value to check
 * @returns {boolean} - True if the value is a local date string
 */
export const isLocalDateStringValue = (value) => {
  return isLocalDateString(value);
};

export default {
  utcToLocalMoment,
  formatUTCDateToLocal,
  convertAPIDatesToLocal,
  convertUTCToLocal,
  localToUTCMoment,
  formatLocalDateToUTC,
  convertAPIDatesToUTC,
};
